import { resolveStagingLineVisibleDescription } from "./dtoolsCatalogueMatch";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";

export type DtoolsChildItemCounts = {
  children: number;
  accessories: number;
};

/** All descendant line ids (not including root). */
export function collectDescendantLineIds(rootId: string, lines: DtoolsStagingLine[]): Set<string> {
  const ids = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const parentId = queue.shift()!;
    for (const line of lines) {
      if (line.parentId === parentId && !ids.has(line.id)) {
        ids.add(line.id);
        queue.push(line.id);
      }
    }
  }
  return ids;
}

export function countChildItems(lineId: string, lines: DtoolsStagingLine[]): DtoolsChildItemCounts {
  const descendants = collectDescendantLineIds(lineId, lines);
  let children = 0;
  let accessories = 0;
  for (const id of descendants) {
    const line = lines.find((row) => row.id === id);
    if (!line) continue;
    if (line.isAccessory) accessories += 1;
    else children += 1;
  }
  return { children, accessories };
}

export function hasDescendantLines(lineId: string, lines: DtoolsStagingLine[]): boolean {
  return collectDescendantLineIds(lineId, lines).size > 0;
}

/** Leaf rows with no value — safe for bulk cleanup. Parents are never empty. */
export function isEmptyStagingLine(line: DtoolsStagingLine, lines: DtoolsStagingLine[]): boolean {
  if (hasDescendantLines(line.id, lines)) return false;
  const qty = Number(line.quantity) || 0;
  const price = Number(line.unitPrice) || 0;
  const total = Number(line.lineTotal) || 0;
  const description = String(resolveStagingLineVisibleDescription(line) ?? "").trim();
  return qty === 0 && price === 0 && total === 0 && description === "";
}

export function findEmptyStagingLines(lines: DtoolsStagingLine[]): DtoolsStagingLine[] {
  return lines.filter((line) => isEmptyStagingLine(line, lines));
}

/** Placeholder descriptions that indicate zero-value junk rows (not strict empty). */
const JUNK_DESCRIPTION_PATTERN =
  /^[-–—_.\s]+$|^(tbd|t\.b\.d\.|n\/a|na|none|nil|null|placeholder|\.+|x+)$/i;

/** Zero-value row with non-empty but meaningless description — manual delete only. */
export function isJunkStagingLine(line: DtoolsStagingLine, lines: DtoolsStagingLine[]): boolean {
  if (isEmptyStagingLine(line, lines)) return false;
  const qty = Number(line.quantity) || 0;
  const price = Number(line.unitPrice) || 0;
  const total = Number(line.lineTotal) || 0;
  if (qty !== 0 || price !== 0 || total !== 0) return false;
  const description = String(resolveStagingLineVisibleDescription(line) ?? "").trim();
  if (!description) return false;
  if (JUNK_DESCRIPTION_PATTERN.test(description)) return true;
  return description.length <= 2;
}

export function findJunkStagingLines(lines: DtoolsStagingLine[]): DtoolsStagingLine[] {
  return lines.filter((line) => isJunkStagingLine(line, lines));
}

export function countRemovableEmptyStagingLines(lines: DtoolsStagingLine[]): number {
  return removeEmptyStagingLines(lines.map((line) => ({ ...line }))).removedIds.length;
}

export const EMPTY_STAGING_CLEANUP_MAX_ITERATIONS = 50;

export type DeleteStagingLinesOptions = {
  /** When true, also removes all lines whose parent chain leads to a deleted id. */
  cascade?: boolean;
};

export function deleteStagingLines(
  lines: DtoolsStagingLine[],
  idsToDelete: Iterable<string>,
  options: DeleteStagingLinesOptions = {},
): DtoolsStagingLine[] {
  const ids = new Set(idsToDelete);
  if (options.cascade) {
    for (const id of [...ids]) {
      for (const descendantId of collectDescendantLineIds(id, lines)) {
        ids.add(descendantId);
      }
    }
  }
  if (ids.size === 0) return lines;
  return lines.filter((line) => !ids.has(line.id));
}

export function removeEmptyStagingLines(lines: DtoolsStagingLine[]): {
  lines: DtoolsStagingLine[];
  removedIds: string[];
} {
  const allRemovedIds: string[] = [];
  let current = lines;
  for (let iteration = 0; iteration < EMPTY_STAGING_CLEANUP_MAX_ITERATIONS; iteration += 1) {
    const empty = findEmptyStagingLines(current);
    if (empty.length === 0) break;
    const batchIds = empty.map((line) => line.id);
    allRemovedIds.push(...batchIds);
    current = deleteStagingLines(current, batchIds);
  }
  return {
    lines: current,
    removedIds: allRemovedIds,
  };
}
